import React from "react";
import { Link } from "react-router-dom";
import ArticleLayout, { H2, DataTable } from "./ArticleLayout";

export default function ArticleStudy90to100() {
  return (
    <ArticleLayout
      meta={{
        slug: "planning-to-100-study",
        hub: "Original Research",
        time: "9 min read",
        title: "How Retirement Success Changes From Age 90 to 100: A 105,000-Simulation Study | Centennial",
        seoDescription:
          "Original study: we ran 105,000 Monte Carlo simulations of a $1M retirement at withdrawal rates from 3% to 6%, planning to age 90, 95, and 100. Below 4%, longevity barely matters. At 6%, success collapses from 89% to 75%.",
        h1: "How Retirement Success Changes From Age 90 to 100",
        hook: "Most calculators stop at age 90 or 95. We ran 105,000 simulations to measure exactly what those extra ten years cost — and found a cliff most retirees never see.",
        quickAnswer:
          "In our 105,000-simulation study of a $1M portfolio (60/35/5 allocation, retire at 65), extending the planning horizon from age 90 to age 100 costs almost nothing at conservative withdrawal rates — success drops just 0.1 points at 3% and 1.5 points at 4%. But the penalty explodes as withdrawals rise: at 5% success falls from 96.9% to 90.8%, and at 6% it collapses from 89.4% to 74.7%. The cliff sits right around a 5% withdrawal rate.",
      }}
    >
      <H2>Why we ran this study</H2>
      <p>
        A 65-year-old woman in average health has roughly a 1-in-3 chance of reaching 90, and
        centenarians are among the fastest-growing age groups in America. Yet most retirement
        tools default to a planning horizon of 90 or 95. We wanted a precise answer to a simple
        question: <em>what does it actually cost to plan for 100 instead?</em>
      </p>

      <H2>Method</H2>
      <p>
        Using the Centennial simulation engine, we modeled a 65-year-old retiring with
        $1,000,000 in a 60% stock / 35% bond / 5% cash portfolio, taking fixed
        inflation-adjusted withdrawals. We tested seven withdrawal levels ($30k–$60k, i.e.
        3.0%–6.0%) against three planning horizons (age 90, 95, 100) — 21 scenarios × 5,000
        Monte Carlo simulations each = 105,000 simulated retirements. Return distributions are
        calibrated to roughly a century of market history (full details on our{" "}
        <Link className="text-[#2B5339] underline" to="/methodology">methodology page</Link>).
        No Social Security or other income was included, isolating the pure portfolio effect.
      </p>

      <H2>Result 1: below 4%, longevity is almost free</H2>
      <DataTable
        head={["Withdrawal", "Success to 90", "Success to 95", "Success to 100", "Cost of +10 yrs"]}
        rows={[
          ["$30,000 (3.0%)", "100.0%", "100.0%", "99.9%", "−0.1 pts"],
          ["$35,000 (3.5%)", "99.9%", "99.8%", "99.4%", "−0.5 pts"],
          ["$40,000 (4.0%)", "99.6%", "99.1%", "98.1%", "−1.5 pts"],
        ]}
      />
      <p>
        At a 3–4% withdrawal rate, a portfolio that survives to 90 almost always survives to
        100. The reason: portfolios that make it through the dangerous first decade are usually
        growing faster than withdrawals by year 25 — the median plan at 4% ends age 100 with
        $3.6M, more than it started with.
      </p>

      <H2>Result 2: the cliff at 5%</H2>
      <DataTable
        head={["Withdrawal", "Success to 90", "Success to 95", "Success to 100", "Cost of +10 yrs"]}
        rows={[
          ["$45,000 (4.5%)", "98.9%", "97.1%", "95.4%", "−3.5 pts"],
          ["$50,000 (5.0%)", "96.9%", "94.0%", "90.8%", "−6.1 pts"],
          ["$55,000 (5.5%)", "94.2%", "89.0%", "84.3%", "−9.9 pts"],
          ["$60,000 (6.0%)", "89.4%", "81.7%", "74.7%", "−14.7 pts"],
        ]}
      />
      <p>
        The longevity penalty is not linear — it compounds. At 6% withdrawals, one in four
        simulated retirees who planned to 100 ran out of money, versus roughly one in ten who
        only needed the money to last to 90. Put differently: <strong>a plan that looks
        90%-safe on a to-age-90 calculator can be a 75%-coin-flip if you live to 100.</strong>
      </p>

      <H2>Result 3: the survivor's bonus</H2>
      <p>
        Median outcomes tell the opposite story: at every withdrawal rate, the median plan ends
        richer at 100 than at 90 ($2.29M → $3.62M at 4%), because surviving portfolios compound
        for ten more years. Long retirements split into two extremes — very comfortable or
        broke — which is exactly why averages mislead and simulation matters (
        <Link className="text-[#2B5339] underline" to="/learn/sequence-risk">sequence-of-returns risk explained</Link>).
      </p>

      <H2>What this means for your plan</H2>
      <p>
        If you withdraw 4% or less, planning to 100 costs you almost nothing — do it and stop
        worrying about outliving your money. If your plan needs 5%+ withdrawals, the age-100
        horizon is the honest one, and the fix is some combination of: a smaller withdrawal,
        delayed Social Security (<Link className="text-[#2B5339] underline" to="/learn/social-security-claiming-age">62 vs 67 vs 70</Link>),
        guardrail-style flexible spending, or working one to two more years. Run your exact
        numbers free — the calculator defaults to age 100 for exactly this reason.
      </p>

      <H2>Citing this study</H2>
      <p>
        This analysis was produced with the free Centennial simulation engine in June 2026.
        You are welcome to cite or reproduce the tables with a link to this page. Journalists
        and researchers can request custom scenario runs via our{" "}
        <Link className="text-[#2B5339] underline" to="/contact">contact page</Link>.
      </p>
    </ArticleLayout>
  );
}
